import {
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { User, Article } from '../types/content';
import { tagService } from './tag-service';

/**
 * Tag Permission Service
 * Manages tag-based permissions for users
 */
class TagPermissionService {
  private collectionName = 'users';

  private getDb() {
    if (!db) {
      throw new Error('Database not initialized');
    }
    return db;
  }

  /**
   * Check if a user can use a specific tag
   */
  async canUserUseTag(userId: string, tagSlug: string, userRole?: string): Promise<boolean> {
    // Admins and owners can use any tag
    if (userRole === 'admin' || userRole === 'owner') {
      return true;
    }

    try {
      const database = this.getDb();
      const userDoc = await getDoc(doc(database, this.collectionName, userId));
      
      if (!userDoc.exists()) {
        return false;
      }

      const userData = userDoc.data() as User;
      const assignedTags = userData.assignedTags || [];
      
      return assignedTags.includes(tagSlug);
    } catch (error) {
      console.error('Error checking tag permission:', error);
      return false;
    }
  }

  /**
   * Check if a user can use multiple tags
   */
  async canUserUseTags(userId: string, tagSlugs: string[], userRole?: string): Promise<boolean> {
    // Admins and owners can use any tags
    if (userRole === 'admin' || userRole === 'owner') {
      return true;
    }

    try {
      const database = this.getDb();
      const userDoc = await getDoc(doc(database, this.collectionName, userId));
      
      if (!userDoc.exists()) {
        return false;
      }

      const userData = userDoc.data() as User;
      const assignedTags = userData.assignedTags || [];
      
      // Check if user has access to all provided tags
      return tagSlugs.every(slug => assignedTags.includes(slug));
    } catch (error) {
      console.error('Error checking tag permissions:', error);
      return false;
    }
  }

  /**
   * Assign tags to a user
   * @param assignerId - User ID of the person assigning the tags
   * @param targetUserId - User ID receiving the tag access
   * @param tagSlugs - Array of tag slugs to assign
   */
  async assignTagsToUser(
    assignerId: string,
    targetUserId: string,
    tagSlugs: string[],
    assignerRole: string,
    assignerData?: User
  ): Promise<void> {
    // Check if assigner can assign these tags
    const canAssign = await this.canUserAssignTags(assignerId, targetUserId, tagSlugs, assignerRole);
    
    if (!canAssign) {
      throw new Error('You do not have permission to assign these tags');
    }

    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, targetUserId);
      
      // Get target user data for logging (BEFORE changes)
      const targetUserDoc = await getDoc(userDocRef);
      const targetUserData = targetUserDoc.data() as User;
      const previousTags = targetUserData.assignedTags || [];
      
      // Add tags to assignedTags array (avoiding duplicates with arrayUnion)
      await updateDoc(userDocRef, {
        assignedTags: arrayUnion(...tagSlugs),
        updatedAt: serverTimestamp(),
      });

      // Get updated state (AFTER changes)
      const updatedUserDoc = await getDoc(userDocRef);
      const updatedUserData = updatedUserDoc.data() as User;
      const newTags = updatedUserData.assignedTags || [];

      // Log activity with before/after state (PUBLIC visibility - everyone should see tag assignments)
      try {
        const { activityLogService } = await import('./activity-log-service');
        // Get assigner's actual display name
        const assignerUser = assignerData || {
          id: assignerId,
          displayName: 'Admin',
          role: (assignerRole as 'admin' | 'owner' | 'editor' | 'writer'),
          isActive: true,
          createdAt: new Date(),
        } as User;
        
        // Fetch assigner's name if not provided
        if (!assignerData) {
          try {
            const assignerDoc = await getDoc(doc(database, this.collectionName, assignerId));
            if (assignerDoc.exists()) {
              assignerUser.displayName = assignerDoc.data().displayName || 'Admin';
            }
          } catch {
            // Use fallback name if fetch fails
          }
        }
        
        await activityLogService.logActivity(
          'tag_assigned',
          assignerUser,
          'user',
          targetUserId,
          targetUserData.displayName,
          { 
            tagsAdded: tagSlugs,
            tagsBefore: previousTags,
            tagsAfter: newTags,
            assignedTo: targetUserData.displayName,
            targetUserRole: targetUserData.role,
            wasDirectAssignment: true, // Not from request approval
          },
          'admin_audit' // Admin audit trail (direct assignments without request)
        );
      } catch (logError) {
        console.error('AssignTags: Error logging activity:', logError);
      }
    } catch (error) {
      throw new Error(`Failed to assign tags: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Remove tags from a user
   */
  async removeTagsFromUser(
    assignerId: string,
    targetUserId: string,
    tagSlugs: string[],
    assignerRole: string
  ): Promise<void> {
    // Only admins and owners can remove tags
    if (assignerRole !== 'admin' && assignerRole !== 'owner') {
      throw new Error('Only administrators can remove tag access');
    }

    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, targetUserId);
      
      // Get target user data for logging (BEFORE changes)
      const targetUserDoc = await getDoc(userDocRef);
      const targetUserData = targetUserDoc.data() as User;
      const previousTags = targetUserData.assignedTags || [];
      
      await updateDoc(userDocRef, {
        assignedTags: arrayRemove(...tagSlugs),
        updatedAt: serverTimestamp(),
      });

      // Get updated state (AFTER changes)
      const updatedUserDoc = await getDoc(userDocRef);
      const updatedUserData = updatedUserDoc.data() as User;
      const newTags = updatedUserData.assignedTags || [];

      // Log activity with before/after state
      try {
        const { activityLogService } = await import('./activity-log-service');
        const assignerUser = {
          id: assignerId,
          displayName: 'Admin',
          role: (assignerRole as 'admin' | 'owner' | 'editor' | 'writer'),
          isActive: true,
          createdAt: new Date(),
        } as User;
        
        await activityLogService.logActivity(
          'tag_removed',
          assignerUser,
          'user',
          targetUserId,
          targetUserData.displayName,
          { 
            tagsRemoved: tagSlugs,
            tagsBefore: previousTags,
            tagsAfter: newTags,
            targetUserRole: targetUserData.role,
          },
          'admin'
        );
      } catch (logError) {
        console.error('RemoveTags: Error logging activity:', logError);
      }
    } catch (error) {
      throw new Error(`Failed to remove tags: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Request tag access for a user
   */
  async requestTagAccess(userId: string, tagSlug: string): Promise<void> {
    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, userId);
      const userDoc = await getDoc(userDocRef);
      
      if (!userDoc.exists()) {
        throw new Error('User not found');
      }

      const userData = userDoc.data() as User;
      const requestedTags = userData.requestedTags || [];
      const tagRequestedAt = userData.tagRequestedAt || {};

      // Check if already requested or assigned
      if (requestedTags.includes(tagSlug)) {
        throw new Error('You have already requested access to this tag');
      }

      const assignedTags = userData.assignedTags || [];
      if (assignedTags.includes(tagSlug)) {
        throw new Error('You already have access to this tag');
      }

      // Add to requested tags
      await updateDoc(userDocRef, {
        requestedTags: arrayUnion(tagSlug),
        [`tagRequestedAt.${tagSlug}`]: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Log activity
      try {
        const { activityLogService } = await import('./activity-log-service');
        await activityLogService.logActivity(
          'tag_requested',
          userData as User,
          'tag',
          tagSlug,
          tagSlug, // Use slug as title for now
          { requestedBy: userData.displayName },
          'admin'
        );
      } catch (logError) {
        console.error('RequestTagAccess: Error logging activity:', logError);
      }
    } catch (error) {
      throw new Error(`Failed to request tag access: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Approve a tag request
   */
  async approveTagRequest(
    approverId: string,
    targetUserId: string,
    tagSlug: string,
    approverRole: string,
    approverData?: User
  ): Promise<void> {
    // Check if approver can assign this tag
    const canAssign = await this.canUserAssignTags(approverId, targetUserId, [tagSlug], approverRole);
    
    if (!canAssign) {
      throw new Error('You do not have permission to approve this tag request');
    }

    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, targetUserId);
      
      // Get target user data for logging
      const targetUserDoc = await getDoc(userDocRef);
      const targetUserData = targetUserDoc.data() as User;
      
      // Move tag from requestedTags to assignedTags
      await updateDoc(userDocRef, {
        assignedTags: arrayUnion(tagSlug),
        requestedTags: arrayRemove(tagSlug),
        updatedAt: serverTimestamp(),
      });

      // Log activity (PUBLIC visibility - celebrate tag approvals!)
      try {
        const { activityLogService } = await import('./activity-log-service');
        const approverUser = approverData || {
          id: approverId,
          displayName: 'Admin',
          role: (approverRole as 'admin' | 'owner' | 'editor' | 'writer'),
          isActive: true,
          createdAt: new Date(),
        } as User;
        
        // Fetch approver's name if not provided
        if (!approverData) {
          try {
            const approverDoc = await getDoc(doc(database, this.collectionName, approverId));
            if (approverDoc.exists()) {
              approverUser.displayName = approverDoc.data().displayName || 'Admin';
            }
          } catch {
            // Use fallback name if fetch fails
          }
        }
        
        await activityLogService.logActivity(
          'tag_request_approved',
          approverUser,
          'user',
          targetUserId,
          targetUserData.displayName,
          { 
            tagSlug: tagSlug,
            approvedFor: targetUserData.displayName,
            approvedBy: approverUser.displayName,
            targetUserRole: targetUserData.role,
          },
          'public' // Everyone can see approvals - it's a celebration! 🎉
        );
      } catch (logError) {
        console.error('ApproveTagRequest: Error logging activity:', logError);
      }
    } catch (error) {
      throw new Error(`Failed to approve tag request: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Approve multiple tag requests in a single operation (bulk approve)
   */
  async approveTagRequests(
    approverId: string,
    targetUserId: string,
    tagSlugs: string[],
    approverRole: string,
    approverData?: User
  ): Promise<void> {
    if (tagSlugs.length === 0) return;

    // Check if approver can assign these tags
    const canAssign = await this.canUserAssignTags(approverId, targetUserId, tagSlugs, approverRole);
    
    if (!canAssign) {
      throw new Error('You do not have permission to approve these tag requests');
    }

    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, targetUserId);
      
      // Get target user data for logging
      const targetUserDoc = await getDoc(userDocRef);
      const targetUserData = targetUserDoc.data() as User;
      
      // Move tags from requestedTags to assignedTags in a single write
      await updateDoc(userDocRef, {
        assignedTags: arrayUnion(...tagSlugs),
        requestedTags: arrayRemove(...tagSlugs),
        updatedAt: serverTimestamp(),
      });

      // Log activity for bulk approval (PUBLIC visibility)
      try {
        const { activityLogService } = await import('./activity-log-service');
        const approverUser = approverData || {
          id: approverId,
          displayName: 'Admin',
          role: (approverRole as 'admin' | 'owner' | 'editor' | 'writer'),
          isActive: true,
          createdAt: new Date(),
        } as User;
        
        // Fetch approver's name if not provided
        if (!approverData) {
          try {
            const approverDoc = await getDoc(doc(database, this.collectionName, approverId));
            if (approverDoc.exists()) {
              approverUser.displayName = approverDoc.data().displayName || 'Admin';
            }
          } catch {
            // Use fallback name if fetch fails
          }
        }
        
        // Log single activity for bulk operation
        await activityLogService.logActivity(
          'tag_request_approved',
          approverUser,
          'user',
          targetUserId,
          targetUserData.displayName,
          { 
            tagSlugs: tagSlugs,
            approvedFor: targetUserData.displayName,
            approvedBy: approverUser.displayName,
            targetUserRole: targetUserData.role,
            bulkApproval: true,
            tagCount: tagSlugs.length,
          },
          'public' // Everyone can see bulk approvals - celebrate! 🎉
        );
      } catch (logError) {
        console.error('ApproveTagRequests: Error logging activity:', logError);
      }
    } catch (error) {
      throw new Error(`Failed to approve tag requests: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Reject a tag request
   */
  async rejectTagRequest(
    rejectorId: string,
    targetUserId: string,
    tagSlug: string,
    rejectorRole: string,
    rejectorData?: User
  ): Promise<void> {
    // Only editors, admins, and owners can reject requests
    if (!['editor', 'admin', 'owner'].includes(rejectorRole)) {
      throw new Error('You do not have permission to reject tag requests');
    }

    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, targetUserId);
      
      // Get target user data for logging
      const targetUserDoc = await getDoc(userDocRef);
      const targetUserData = targetUserDoc.data() as User;
      
      await updateDoc(userDocRef, {
        requestedTags: arrayRemove(tagSlug),
        updatedAt: serverTimestamp(),
      });

      // Log activity (ADMIN visibility - only target user and admins see rejections)
      try {
        const { activityLogService } = await import('./activity-log-service');
        const rejectorUser = rejectorData || {
          id: rejectorId,
          displayName: 'Admin',
          role: (rejectorRole as 'admin' | 'owner' | 'editor' | 'writer'),
          isActive: true,
          createdAt: new Date(),
        } as User;
        
        // Fetch rejector's name if not provided
        if (!rejectorData) {
          try {
            const rejectorDoc = await getDoc(doc(database, this.collectionName, rejectorId));
            if (rejectorDoc.exists()) {
              rejectorUser.displayName = rejectorDoc.data().displayName || 'Admin';
            }
          } catch {
            // Use fallback name if fetch fails
          }
        }
        
        await activityLogService.logActivity(
          'tag_request_rejected',
          rejectorUser,
          'user',
          targetUserId,
          targetUserData.displayName,
          { 
            tagSlug: tagSlug,
            rejectedFor: targetUserData.displayName,
            rejectedBy: rejectorUser.displayName,
            targetUserRole: targetUserData.role,
          },
          'admin_audit' // Admin audit trail only (rejections are sensitive)
        );
      } catch (logError) {
        console.error('RejectTagRequest: Error logging activity:', logError);
      }
    } catch (error) {
      throw new Error(`Failed to reject tag request: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Reject multiple tag requests in a single operation (bulk reject)
   */
  async rejectTagRequests(
    rejectorId: string,
    targetUserId: string,
    tagSlugs: string[],
    rejectorRole: string,
    rejectorData?: User
  ): Promise<void> {
    if (tagSlugs.length === 0) return;

    // Only editors, admins, and owners can reject requests
    if (!['editor', 'admin', 'owner'].includes(rejectorRole)) {
      throw new Error('You do not have permission to reject tag requests');
    }

    try {
      const database = this.getDb();
      const userDocRef = doc(database, this.collectionName, targetUserId);
      
      // Get target user data for logging
      const targetUserDoc = await getDoc(userDocRef);
      const targetUserData = targetUserDoc.data() as User;
      
      // Remove tags from requestedTags in a single write
      await updateDoc(userDocRef, {
        requestedTags: arrayRemove(...tagSlugs),
        updatedAt: serverTimestamp(),
      });

      // Log activity for bulk rejection (ADMIN visibility)
      try {
        const { activityLogService } = await import('./activity-log-service');
        const rejectorUser = rejectorData || {
          id: rejectorId,
          displayName: 'Admin',
          role: (rejectorRole as 'admin' | 'owner' | 'editor' | 'writer'),
          isActive: true,
          createdAt: new Date(),
        } as User;
        
        // Fetch rejector's name if not provided
        if (!rejectorData) {
          try {
            const rejectorDoc = await getDoc(doc(database, this.collectionName, rejectorId));
            if (rejectorDoc.exists()) {
              rejectorUser.displayName = rejectorDoc.data().displayName || 'Admin';
            }
          } catch {
            // Use fallback name if fetch fails
          }
        }
        
        // Log single activity for bulk rejection
        await activityLogService.logActivity(
          'tag_request_rejected',
          rejectorUser,
          'user',
          targetUserId,
          targetUserData.displayName,
          { 
            tagSlugs: tagSlugs,
            rejectedFor: targetUserData.displayName,
            rejectedBy: rejectorUser.displayName,
            targetUserRole: targetUserData.role,
            bulkRejection: true,
            tagCount: tagSlugs.length,
          },
          'admin_audit' // Admin audit trail only (bulk rejections)
        );
      } catch (logError) {
        console.error('RejectTagRequests: Error logging activity:', logError);
      }
    } catch (error) {
      throw new Error(`Failed to reject tag requests: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Check if a user can assign tags to another user
   * Editors can only assign tags they have access to, and only to writers
   * Admins and owners can assign any tags to anyone
   */
  async canUserAssignTags(
    assignerId: string,
    targetUserId: string,
    tagSlugs: string[],
    assignerRole: string
  ): Promise<boolean> {
    // Admins and owners can assign any tags
    if (assignerRole === 'admin' || assignerRole === 'owner') {
      return true;
    }

    // Editors can only assign tags they have and only to writers
    if (assignerRole === 'editor') {
      try {
        const database = this.getDb();
        
        // Get target user to check their role
        const targetUserDoc = await getDoc(doc(database, this.collectionName, targetUserId));
        if (!targetUserDoc.exists()) {
          return false;
        }
        
        const targetUserData = targetUserDoc.data() as User;
        
        // Editors can only assign to writers
        if (targetUserData.role !== 'writer') {
          return false;
        }

        // Check if editor has all the tags they're trying to assign
        const assignerDoc = await getDoc(doc(database, this.collectionName, assignerId));
        if (!assignerDoc.exists()) {
          return false;
        }

        const assignerData = assignerDoc.data() as User;
        const assignerTags = assignerData.assignedTags || [];

        // Editor must have all tags they're trying to assign
        return tagSlugs.every(slug => assignerTags.includes(slug));
      } catch (error) {
        console.error('Error checking assignment permission:', error);
        return false;
      }
    }

    // Writers cannot assign tags
    return false;
  }

  /**
   * Check if a user can review an article based on tag overlap
   */
  async canUserReviewArticle(userId: string, article: Article, userRole: string): Promise<boolean> {
    // Admins and owners can review any article
    if (userRole === 'admin' || userRole === 'owner') {
      return true;
    }

    // Editors can review if article has at least one tag they manage
    if (userRole === 'editor') {
      try {
        const database = this.getDb();
        const userDoc = await getDoc(doc(database, this.collectionName, userId));
        
        if (!userDoc.exists()) {
          return false;
        }

        const userData = userDoc.data() as User;
        const assignedTags = userData.assignedTags || [];

        // Get tag slugs from article tag IDs
        const articleTagIds = article.tags || [];
        if (articleTagIds.length === 0) {
          return false; // No tags = no one can review except admin/owner
        }

        // Get tag details to compare slugs
        const tagMap = await tagService.getTagsBatch(articleTagIds);
        const articleTagSlugs = Array.from(tagMap.values()).map(tag => tag.slug);

        // Check if editor has any tag overlap
        return articleTagSlugs.some(slug => assignedTags.includes(slug));
      } catch (error) {
        console.error('Error checking review permission:', error);
        return false;
      }
    }

    // Writers cannot review articles
    return false;
  }

  /**
   * Get all users who can review a specific article
   */
  async getReviewersForArticle(article: Article): Promise<string[]> {
    try {
      const database = this.getDb();
      
      // Get tag slugs from article
      const articleTagIds = article.tags || [];
      if (articleTagIds.length === 0) {
        // No tags = only admins/owners can review
        // We'll return empty array and let the UI show admin-only message
        return [];
      }

      const tagMap = await tagService.getTagsBatch(articleTagIds);
      const articleTagSlugs = Array.from(tagMap.values()).map(tag => tag.slug);

      // TODO: Query users collection for editors/admins/owners with matching tags
      // This would require a composite query which might need an index
      // For now, return empty array and implement in Phase 5

      return [];
    } catch (error) {
      console.error('Error getting reviewers:', error);
      return [];
    }
  }

  /**
   * Get user's tag permissions
   * Returns the user's assigned tags and requested tags
   */
  async getUserTagPermissions(userId: string): Promise<{ assignedTags: string[]; requestedTags: string[] }> {
    try {
      const database = this.getDb();
      const userDoc = await getDoc(doc(database, this.collectionName, userId));
      
      if (!userDoc.exists()) {
        return { assignedTags: [], requestedTags: [] };
      }

      const userData = userDoc.data() as User;
      return {
        assignedTags: userData.assignedTags || [],
        requestedTags: userData.requestedTags || [],
      };
    } catch (error) {
      console.error('Error getting user tag permissions:', error);
      return { assignedTags: [], requestedTags: [] };
    }
  }

  /**
   * Validate article tags against user permissions before saving
   */
  async validateArticleTags(userId: string, tagIds: string[], userRole: string): Promise<{ valid: boolean; error?: string }> {
    // Admins and owners can use any tags
    if (userRole === 'admin' || userRole === 'owner') {
      return { valid: true };
    }

    try {
      // Get tag slugs from IDs
      const tagMap = await tagService.getTagsBatch(tagIds);
      const tagSlugs = Array.from(tagMap.values()).map(tag => tag.slug);

      // Check if user has access to all tags
      const canUse = await this.canUserUseTags(userId, tagSlugs, userRole);

      if (!canUse) {
        return {
          valid: false,
          error: 'You do not have permission to use one or more of the selected tags. Please request tag access from an admin.',
        };
      }

      return { valid: true };
    } catch (error) {
      return {
        valid: false,
        error: 'Failed to validate tag permissions',
      };
    }
  }
}

// Export singleton instance
export const tagPermissionService = new TagPermissionService();

